// Global event stream client (architecture §8.4, §21.4, WP0). One EventSource per tab; default handlers patch the
// TanStack Query caches and the zustand stores. Screens add their own handlers with `onEvent(type, handler)`.
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  AgentSummary, ApprovalView, BootstrapResponse, HqEvent, HqEventOf, HqEventType, IntegrationStatus, MessageView,
  TaskView, ThreadSummary,
} from '@hq/shared';
import { useBadgeStore } from '../state/badges.ts';
import { useRunStore } from '../state/run.ts';
import { toast, useUiStore } from '../state/ui.ts';
import { qk } from './queryKeys.ts';

/** One page of GET /api/threads/:id/messages (messages ascending by seq; `['messages', id]` is infinite, newest page first). */
export interface MessagesPage { messages: MessageView[]; hasMore: boolean }
/** Optimistic composer rows use ids with this prefix; the matching `message.created` replaces them (§21.4). */
export const OPTIMISTIC_PREFIX = 'tmp:';

type Handler<T extends HqEventType> = (e: HqEventOf<T>) => void;
const handlers = new Map<HqEventType, Set<(e: HqEvent) => void>>();

/** Subscribes to one event type; returns the unsubscribe function. */
export function onEvent<T extends HqEventType>(type: T, handler: Handler<T>): () => void {
  let set = handlers.get(type);
  if (!set) handlers.set(type, (set = new Set()));
  const h = handler as (e: HqEvent) => void;
  set.add(h);
  return () => void set.delete(h);
}

/** Dispatches an event to the default cache handlers and then to all subscribers (exported for tests). */
export function dispatchEvent(qc: QueryClient, e: HqEvent): void {
  try {
    applyDefault(qc, e);
  } catch (err) {
    console.error('SSE-Handler', e.type, err);
  }
  for (const h of handlers.get(e.type) ?? []) {
    try {
      h(e);
    } catch (err) {
      console.error('SSE-Abonnent', e.type, err);
    }
  }
}

// ------------------------------------------------------------------ cache helpers

const textOf = (m: MessageView) => m.parts.map((p) => (p.type === 'text' ? p.md : '')).join('');

/** Appends (or replaces) a persisted message in the infinite messages cache. Exported for tests and WP4. */
export function upsertMessage(data: InfiniteData<MessagesPage> | undefined, msg: MessageView): InfiniteData<MessagesPage> | undefined {
  if (!data || data.pages.length === 0) return data;
  let found = false;
  const pages = data.pages.map((p) => {
    if (!p.messages.some((m) => m.id === msg.id)) return p;
    found = true;
    return { ...p, messages: p.messages.map((m) => (m.id === msg.id ? msg : m)) };
  });
  if (found) return { ...data, pages };
  const [newest, ...rest] = pages as [MessagesPage, ...MessagesPage[]];
  let list = newest.messages;
  if (msg.author === 'chris') {
    // Replace the oldest optimistic row with the same text (the composer's temporary bubble).
    const text = textOf(msg);
    const idx = list.findIndex((m) => m.id.startsWith(OPTIMISTIC_PREFIX) && textOf(m) === text);
    if (idx >= 0) list = list.filter((_, i) => i !== idx);
  }
  list = [...list, msg].sort((a, b) => (a.id.startsWith(OPTIMISTIC_PREFIX) ? 1 : 0) - (b.id.startsWith(OPTIMISTIC_PREFIX) ? 1 : 0) || a.seq - b.seq);
  return { ...data, pages: [{ ...newest, messages: list }, ...rest] };
}

function patchList<T extends { id: string }>(qc: QueryClient, prefix: readonly unknown[], item: T): boolean {
  let missing = false;
  qc.setQueriesData<T[]>({ queryKey: prefix }, (old) => {
    if (!Array.isArray(old)) return old;
    if (!old.some((x) => x.id === item.id)) {
      missing = true;
      return old;
    }
    return old.map((x) => (x.id === item.id ? { ...x, ...item } : x));
  });
  return !missing;
}

function patchAgent(qc: QueryClient, agentId: string, patch: Partial<AgentSummary>): void {
  qc.setQueryData<AgentSummary[]>(qk.agents(), (old) => old?.map((a) => (a.id === agentId ? { ...a, ...patch } : a)));
  qc.setQueryData<AgentSummary>(qk.agent(agentId), (old) => (old ? { ...old, ...patch } : old));
  qc.setQueryData<BootstrapResponse>(qk.bootstrap(), (old) =>
    old ? { ...old, agents: old.agents.map((a) => (a.id === agentId ? { ...a, ...patch } : a)) } : old);
}

function applyDefault(qc: QueryClient, e: HqEvent): void {
  switch (e.type) {
    case 'hello':
    case 'reload':
      return; // handled by the connection (needs connection state)
    case 'run': {
      useRunStore.getState().apply({ runId: e.runId, threadId: e.threadId, agentId: e.agentId }, e.event);
      if (e.event.type === 'message.discarded' && e.event.reason === 'refusal') toast({ tone: 'urgent', text: 'Claude hat das abgelehnt.' });
      if (e.event.type === 'run.finished') void qc.invalidateQueries({ queryKey: qk.thread(e.threadId) });
      return;
    }
    case 'message.created': {
      qc.setQueryData<InfiniteData<MessagesPage>>(qk.messages(e.threadId), (old) => upsertMessage(old, e.message));
      useRunStore.getState().messagePersisted(e.threadId, e.message.id);
      return;
    }
    case 'message.updated': {
      qc.setQueryData<InfiniteData<MessagesPage>>(qk.messages(e.threadId), (old) => old && {
        ...old,
        pages: old.pages.map((p) => ({
          ...p,
          messages: e.hidden
            ? p.messages.filter((m) => m.id !== e.messageId)
            : p.messages.map((m) => (m.id === e.messageId ? { ...m, parts: e.parts } : m)),
        })),
      });
      return;
    }
    case 'thread.changed': {
      qc.setQueryData<ThreadSummary>(qk.thread(e.thread.id), (old) => (old ? { ...old, ...e.thread } : old));
      if (!patchList(qc, ['threads'], e.thread)) void qc.invalidateQueries({ queryKey: ['threads'] });
      return;
    }
    case 'agent.status':
      patchAgent(qc, e.agentId, { live: e.live });
      return;
    case 'approval.changed': {
      qc.setQueryData<ApprovalView>(qk.approval(e.approval.id), e.approval);
      // Status filters (offen/erledigt) decide list membership on the server; refetch when it is not in a list yet
      // or moved between filters.
      patchList(qc, ['approvals'], e.approval);
      void qc.invalidateQueries({ queryKey: ['approvals'], exact: false, refetchType: 'active' });
      return;
    }
    case 'task.changed': {
      qc.setQueryData<TaskView>(qk.task(e.task.id), (old) => (old ? { ...old, ...e.task } : old));
      void qc.invalidateQueries({ queryKey: qk.task(e.task.id) });
      if (!patchList(qc, ['tasks'], e.task)) void qc.invalidateQueries({ queryKey: ['tasks'] });
      return;
    }
    case 'report.created':
      void qc.invalidateQueries({ queryKey: ['reports'] });
      return;
    case 'notification.created':
      return; // counts arrive separately; pushes and IncomingCall are WP3
    case 'call.changed':
      qc.setQueryData(qk.call(e.call.id), e.call);
      return;
    case 'budget.changed':
      qc.setQueryData(qk.budget(), e.budget);
      qc.setQueryData<BootstrapResponse>(qk.bootstrap(), (old) => (old ? { ...old, budget: e.budget } : old));
      return;
    case 'integration.changed': {
      const upd = (list: IntegrationStatus[]) => list.map((i) => (i.key === e.integration.key ? e.integration : i));
      qc.setQueryData<IntegrationStatus[]>(qk.integrations(), (old) => old && upd(old));
      qc.setQueryData<BootstrapResponse>(qk.bootstrap(), (old) => (old ? { ...old, integrations: upd(old.integrations) } : old));
      void qc.invalidateQueries({ queryKey: qk.integration(e.integration.key), exact: true });
      return;
    }
    case 'settings.changed':
      void qc.invalidateQueries({ queryKey: qk.settings() });
      void qc.invalidateQueries({ queryKey: qk.bootstrap() });
      if (e.keys.some((k) => k.startsWith('models') || k.startsWith('budget'))) {
        void qc.invalidateQueries({ queryKey: qk.models() });
        void qc.invalidateQueries({ queryKey: qk.budget() });
      }
      return;
    case 'cockpit.invalidate':
      void qc.invalidateQueries({ queryKey: ['cockpit'] });
      return;
    case 'voice.card':
      return; // WP3 subscribes
    case 'counts': {
      const { approvals, unreadThreads, unreadReports, notifications } = e;
      useBadgeStore.getState().setCounts({ approvals, unreadThreads, unreadReports, notifications });
      qc.setQueryData<BootstrapResponse>(qk.bootstrap(), (old) => (old ? { ...old, counts: { approvals, unreadThreads, unreadReports, notifications } } : old));
      return;
    }
  }
}

// ------------------------------------------------------------------ connection

export interface EventsConnection {
  close(): void;
  /** Version of the server from the first `hello` of this page load. */
  readonly version: string | null;
}

let current: (EventsConnection & { reopenIfNeeded(): void }) | null = null;

/** Called after a reconnect: rebuilds volatile state from the server (§8.3). */
function resync(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: qk.bootstrap() });
  // Runs that started or finished while the stream was down sent no event (HANDOFF-WP4 §11).
  void qc.invalidateQueries({ queryKey: ['runs'] });
  void qc.invalidateQueries({ queryKey: ['run'] });
  const open = useUiStore.getState().openThreadId;
  if (open) {
    void qc.invalidateQueries({ queryKey: qk.thread(open) });
    void qc.invalidateQueries({ queryKey: qk.messages(open) });
    useRunStore.getState().clear(open);
  }
}

/**
 * Opens the single EventSource of this tab (idempotent). Reconnects are handled by the browser; on iOS the stream dies
 * in the background, so `visibilitychange → visible` re-opens it when it is not OPEN. A closed stream (for example 401)
 * is retried with back-off and makes the auth state refetch.
 */
export function connectEvents(qc: QueryClient, url = '/api/events'): EventsConnection {
  if (current) return current;
  if (typeof EventSource === 'undefined') return { close: () => undefined, version: null };

  let es: EventSource | null = null;
  let hellos = 0;
  let version: string | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let backoff = 2000;
  let closed = false;

  const open = () => {
    if (closed) return;
    es?.close();
    es = new EventSource(url, { withCredentials: true });
    es.onmessage = (msg: MessageEvent<string>) => {
      let ev: HqEvent;
      try {
        ev = JSON.parse(msg.data) as HqEvent;
      } catch {
        return;
      }
      if (ev.type === 'hello') {
        backoff = 2000;
        hellos += 1;
        if (version === null) version = ev.version;
        else if (ev.version !== version) useUiStore.getState().setUpdateAvailable(true);
        if (hellos > 1) resync(qc);
      } else if (ev.type === 'reload') {
        if (ev.reason === 'version') useUiStore.getState().setUpdateAvailable(true);
        else void qc.invalidateQueries();
      }
      dispatchEvent(qc, ev);
    };
    es.onerror = () => {
      if (!es || es.readyState !== EventSource.CLOSED || closed) return;
      // The browser gave up (HTTP error such as 401 or 503): check the session, retry later.
      void qc.invalidateQueries({ queryKey: qk.authState() });
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(open, backoff);
      backoff = Math.min(backoff * 2, 60_000);
    };
  };

  const reopenIfNeeded = () => {
    if (closed || document.visibilityState !== 'visible') return;
    if (!es || es.readyState !== EventSource.OPEN) open();
  };
  const onVisibility = () => reopenIfNeeded();
  const onOnline = () => reopenIfNeeded();
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('online', onOnline);
  open();

  current = {
    get version() {
      return version;
    },
    reopenIfNeeded,
    close() {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      es?.close();
      es = null;
      current = null;
    },
  };
  return current;
}

/** Closes the stream (logout). */
export function disconnectEvents(): void {
  current?.close();
}
